/**
 * Card stagger animation utility.
 * Uses IntersectionObserver so cards animate only when they scroll into view.
 */

const STAGGER_DELAY = 65; // ms between each card in a visible batch
const ANIMATED_ATTR = 'data-stagger-animated';

let observer = null;
let pendingBatch = [];
let batchTimer = null;

function getObserver() {
  if (observer) return observer;

  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      pendingBatch.push(entry.target);

      // Batch cards that enter viewport around the same time, then stagger them
      if (batchTimer) clearTimeout(batchTimer);
      batchTimer = setTimeout(animateBatch, 30);
    });
  }, { threshold: 0.05 });

  return observer;
}

function animateBatch() {
  const cards = pendingBatch.splice(0);
  cards.forEach((card, i) => {
    const delay = i * STAGGER_DELAY;
    card.style.animationDelay = delay + 'ms';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        card.classList.remove('card-stagger-hidden');
        card.classList.add('card-stagger-in');
      });
    });

    card.addEventListener('animationend', function onEnd() {
      card.style.animationDelay = '';
      card.classList.remove('card-stagger-in');
    }, { once: true });
  });
}

/**
 * Animate cards within a container that haven't been animated yet.
 * Cards will fade-in + slide-up only when they scroll into the viewport.
 * @param {string} containerSelector - CSS selector for the cards container
 * @param {string} cardSelector - CSS selector for individual cards
 */
export function staggerCards(containerSelector, cardSelector) {
  const container = document.querySelector(containerSelector);
  if (!container) return;

  const selector = cardSelector || '.card, .discussion-card';
  const obs = getObserver();

  container.querySelectorAll(selector).forEach((card) => {
    if (card.hasAttribute(ANIMATED_ATTR)) return;
    card.setAttribute(ANIMATED_ATTR, '1');
    card.classList.add('card-stagger-hidden');
    obs.observe(card);
  });
}

// Mark first-page cards on load so they're skipped during pagination
document.addEventListener('DOMContentLoaded', () => {
  const containers = [
    ['.deals-list'],
    ['.list-view', '.card, .discussion-card'],
    ['#coupons-list-content'],
    ['.coupons-list'],
    ['.all-deals-list'],
    ['.all-coupons-list']
  ];
  containers.forEach(([sel, cardSel]) => {
    const container = document.querySelector(sel);
    if (!container) return;
    const cs = cardSel || '.card, .discussion-card';
    container.querySelectorAll(cs).forEach((card) => {
      card.setAttribute(ANIMATED_ATTR, '1');
    });
  });
});

// Make available globally for .js.erb responses
window.staggerCards = staggerCards;
